import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';
import { Transaction } from '../../models/transaction';
import {
  buildTransactionTypeFilterOptions,
  resolveTransactionTypeLabel,
  TransactionListFilters,
  TransactionService,
} from '../../services/transaction.service';
import { AdminPageHeaderComponent } from '../shared/admin-page-header/admin-page-header.component';
import { AccountService } from '../../services/account.service';
import {
  desiredNavigateParamsToShape,
  formatCreatedAtForTable as formatTxCreatedAtForTable,
  readListStateFromParams,
  shouldSkipTxListNavigation,
} from './transactions-list.helpers';
import { DatePickerModule } from 'primeng/datepicker';

function pickUserDisplayName(transaction: Transaction): string | null {
  const u = transaction.account?.username?.trim();
  if (u) {
    return u;
  }
  const e = transaction.account?.email?.trim();
  if (e) {
    return e;
  }
  const fn = transaction.full_name?.trim();
  if (fn && fn !== 'None None') {
    return fn;
  }
  return null;
}

@Component({
  selector: 'app-transactions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TableModule,
    ProgressSpinnerModule,
    AdminPageHeaderComponent,
    DatePickerModule,
  ],
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss'],
})
export class TransactionsComponent {
  private readonly legacyAllTypesInQuery = 'ALL';
  readonly title = 'Transações';
  readonly subtitle = 'Filtros opcionais na listagem; exportar CSV inclui todas as linhas que batem com os filtros.';

  readonly typeOptions = buildTransactionTypeFilterOptions();
  readonly resolveTransactionTypeLabel = resolveTransactionTypeLabel;

  rows: Transaction[] = [];
  isLoading = false;
  isExporting = false;
  errorMessage = '';

  totalItems = 0;
  page = 1;
  rowsPerPage = 10;
  statusFilter = '';
  searchTerm = '';
  typeFilter = '';
  trustedFilter: '' | '1' | '0' = '';
  riskFilter: '' | '1' | '0' = '';

  startDate: Date | null = null;
  endDate: Date | null = null;

  private formatDateForApi(date: Date | null): string | undefined {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDateFromUrl(value: string | null): Date | null {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  returnToTransactionId: string | null = null;

  readonly statusOptions = [
    { label: 'Todos', value: '' },
    { label: 'Aprovado', value: 'APPROVED' },
    { label: 'Pendente', value: 'PENDING' },
    { label: 'Rejeitado', value: 'REJECTED' },
  ];

  readonly trustedOptions = [
    { label: 'Todos', value: '' as const },
    { label: 'Trusted', value: '1' as const },
    { label: 'Não trusted', value: '0' as const },
  ];

  readonly riskOptions = [
    { label: 'Todos', value: '' as const },
    { label: 'Com risco aberto', value: '1' as const },
    { label: 'Sem risco aberto', value: '0' as const },
  ];

  get canCreateAdminTransaction(): boolean {
    return this.accountService.canCreateAdminTransaction();
  }

  rowHasRisk(t: Transaction): boolean {
    const r = t as Transaction & { risk?: boolean };
    return r.risk === true || t.has_open_risk === true || t.has_risk === true;
  }

  riskTooltip(t: Transaction): string {
    const lines = t.risk_alerts?.filter((s) => String(s).trim()) ?? [];
    if (lines.length) {
      return lines.join('\n');
    }
    return 'Conta com risco ou alerta aberto';
  }

  readonly formatCreatedAtForTable = formatTxCreatedAtForTable;

  constructor(
    private transactionService: TransactionService,
    private router: Router,
    private route: ActivatedRoute,
    private accountService: AccountService,
    private cdr: ChangeDetectorRef
  ) {
    this.route.queryParams
      .pipe(
        distinctUntilChanged((a, b) => TransactionsComponent.sameQueryParams(a, b)),
        tap((params) => this.applyStateFromQueryParams(params)),
        switchMap(() => {
          this.errorMessage = '';
          this.isLoading = true;
          const filters: TransactionListFilters = {
            ...this.buildListFiltersWithoutPagination(),
            page: this.page,
            page_size: this.rowsPerPage,
          };
          return this.transactionService.listTransactions(filters).pipe(
            finalize(() => {
              this.isLoading = false;
            })
          );
        })
      )
      .subscribe({
        next: (response) => {
          this.rows = [...(response.results ?? [])];
          this.totalItems = response.count ?? 0;
          this.cdr.detectChanges();
        },
        error: () => {
          this.errorMessage = 'Não foi possível carregar as transações.';
          this.cdr.detectChanges();
        },
      });
  }

  get tableFirst(): number {
    return Math.max(0, (this.page - 1) * this.rowsPerPage);
  }

  private static sameQueryParams(a: Params, b: Params): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
      const va = a[k];
      const vb = b[k];
      const sa = Array.isArray(va) ? va.join(',') : String(va ?? '');
      const sb = Array.isArray(vb) ? vb.join(',') : String(vb ?? '');
      if (sa !== sb) {
        return false;
      }
    }
    return true;
  }

  private applyStateFromQueryParams(params: Params): void {
    const s = readListStateFromParams(params, this.legacyAllTypesInQuery);
    this.statusFilter = s.statusFilter;
    this.searchTerm = s.searchTerm;
    this.typeFilter = s.typeFilter;
    this.trustedFilter = s.trustedFilter;
    this.riskFilter = s.riskFilter;
    this.page = s.page;
    this.returnToTransactionId = s.returnToTransactionId;
    this.startDate = this.parseDateFromUrl(s.startDate);
    this.endDate = this.parseDateFromUrl(s.endDate);
  }

  goBackToTransactionDetail(): void {
    const id = this.returnToTransactionId;
    if (id == null) {
      return;
    }
    this.router.navigate(['/dashboard/transactions', id]);
  }

  onApplyFilters(): void {
    this.page = 1;
    this.updateQueryParams();
  }

  clearFilters(): void {
    this.statusFilter = '';
    this.searchTerm = '';
    this.typeFilter = '';
    this.trustedFilter = '';
    this.riskFilter = '';
    this.startDate = null;
    this.endDate = null;
    this.page = 1;
    this.updateQueryParams();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.rowsPerPage;
    const nextPage = Math.floor(first / rows) + 1;

    if (rows !== this.rowsPerPage) {
      this.rowsPerPage = rows;
      this.page = nextPage;
      this.updateQueryParams();
      return;
    }
    if (nextPage === this.page) {
      return;
    }
    this.page = nextPage;
    this.updateQueryParams();
  }

  getStatusLabel(status?: string): string {
    if (!status) {
      return '-';
    }
    if (status === 'FAILED') {
      return 'REJECTED';
    }
    return status;
  }

  onTransactionRowClick(transaction: Transaction, event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a,button,input,select,textarea')) {
      return;
    }
    this.onTransactionRowActivate(transaction);
  }

  onTransactionRowActivate(transaction: Transaction): void {
    const id = transaction.id;
    if (id == null) {
      return;
    }
    this.router.navigate(['/dashboard/transactions', id]);
  }

  getUserDisplay(transaction: Transaction): string {
    const id = transaction.account_id ?? transaction.user_id ?? transaction.account?.id;
    const idStr = id != null ? String(id) : null;
    const name = pickUserDisplayName(transaction);
    if (name && idStr) {
      return `${name} (${idStr})`;
    }
    if (name) {
      return name;
    }
    if (idStr) {
      return `(${idStr})`;
    }
    return '-';
  }

  exportCsv(): void {
    if (this.isExporting || this.isLoading) {
      return;
    }
    this.isExporting = true;
    this.errorMessage = '';

    const filters = this.buildListFiltersWithoutPagination();

    this.transactionService
      .exportTransactionsCsv(filters)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: ({ blob, filename }) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename?.trim() || 'transacoes_export.csv';
          link.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.errorMessage = 'Não foi possível exportar as transações.';
        },
      });
  }

  private buildListFiltersWithoutPagination(): Omit<TransactionListFilters, 'page' | 'page_size'> {
    return {
      status: this.statusFilter || undefined,
      search: this.searchTerm.trim() || undefined,
      transaction_type: this.typeFilter.trim() || undefined,
      trusted: this.trustedFilter || undefined,
      has_open_risk: this.riskFilter || undefined,
      start_date: this.formatDateForApi(this.startDate),
      end_date: this.formatDateForApi(this.endDate),
    };
  }

  private updateQueryParams(): void {
    const typeTrimmed = this.typeFilter.trim();
    const queryParams: Record<string, string | number | null> = {
      page: this.page,
      status: this.statusFilter || null,
      search: this.searchTerm.trim() || null,
      transaction_type: typeTrimmed === '' ? null : typeTrimmed,
      trusted: this.trustedFilter || null,
      has_open_risk: this.riskFilter || null,
      from_transaction: this.returnToTransactionId != null ? this.returnToTransactionId : null,
      start_date: this.startDate ? this.formatDateForApi(this.startDate) || null : null,
      end_date: this.endDate ? this.formatDateForApi(this.endDate) || null : null,
    };

    const desired = desiredNavigateParamsToShape(queryParams);
    if (shouldSkipTxListNavigation(this.route.snapshot.queryParamMap, desired)) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });
  }
}
